import type { UserProfile, UserProfileRead } from "epsm-api-types/models";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { UseData } from "../../services/UseData";

export type UserDataType = ReturnType<typeof useUserData>;

export const useUserData = () => {
	const { isAuthenticated } = useAuth();

	const { execute: executeGet, isLoading: isLoadingGet } =
		UseData.create().useGetData();
	const { execute: executeUpdate, isLoading: isLoadingUpdate } =
		UseData.create().useUpdateData();

	const [userData, setUserData] = useState<Partial<
		UserProfile & UserProfileRead
	> | null>(null);

	const fetchData = useCallback(async () => {
		try {
			const fetchedData = await executeGet();

			if (fetchedData) {
				setUserData(fetchedData);
			}
		} catch (error) {
			ErrorHandler.logError(error);
		}
	}, [executeGet]);

	useEffect(() => {
		if (isAuthenticated) {
			fetchData();
		}
	}, [isAuthenticated, fetchData]);

	const updateUserData = useCallback(
		async (updatedFields: Partial<UserProfile>) => {
			try {
				await executeUpdate(updatedFields);
				setUserData((prev) =>
					prev
						? {
								...prev,
								...updatedFields,
							}
						: prev,
				);
			} catch (error) {
				ErrorHandler.logError(error);
				return Promise.reject(error);
			}
		},
		[executeUpdate],
	);

	return {
		userData,
		setUserData,
		isLoadingGet,
		isLoadingUpdate,
		updateUserData,
		fetchUserProfile: fetchData,
	};
};
